import {
  Edit,
  Key,
  KeyRound,
  Mail,
  Monitor,
  MoreHorizontal,
  Package,
  Scan,
  Server,
  Shield,
  ShieldCheck,
  User,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import { isCenterLike } from "@/features/servers/utils/ficha";
import type { UserAction } from "@/features/users/components/UserActionsDialog";
import { UserInstallationsDialog } from "./UserInstallationsDialog";
import type { User as UserType } from "@/features/users/schemas/user";

import type { ColumnDef } from "@tanstack/react-table";

import { getUserFullName, getUserInitials } from "@/features/users/utils/user-utils";

const getUserRole = (user: UserType): string => {
  const roleMap: Record<UserType["type"], string> = {
    admin: "Admin",
    physician: "Médico",
    patient: "Paciente",
    institution: "Institución",
  };
  return roleMap[user.type] || "Usuario";
};

const formatSimpleDate = (date: any): string => {
  if (!date) return "N/A";
  try {
    let d: Date;
    if (date && typeof date === "object") {
      const seconds = date.seconds ?? date._seconds;
      if (typeof seconds === "number") {
        d = new Date(seconds * 1000);
      } else {
        d = date instanceof Date ? date : new Date(date);
      }
    } else {
      d = date instanceof Date ? date : new Date(date);
    }

    if (isNaN(d.getTime())) return "N/A";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return "N/A";
  }
};

const formatDate = (date: any): string => {
  if (!date) return "Nunca";
  try {
    let d: Date;
    if (date && typeof date === "object") {
      const seconds = date.seconds ?? date._seconds;
      if (typeof seconds === "number") {
        d = new Date(seconds * 1000);
      } else {
        d = date instanceof Date ? date : new Date(date);
      }
    } else {
      d = date instanceof Date ? date : new Date(date);
    }

    if (isNaN(d.getTime())) return "Fecha inválida";

    return d.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Fecha inválida";
  }
};

export interface UsersTableActions {
  onEdit: (user: UserType) => void;
  onView: (user: UserType) => void;
  onAction: (user: UserType, action: UserAction) => void;
  onViewImagings?: (user: UserType) => void;
  currentUser?: UserType | null;
}

/**
 * Sub-component for the actions cell — manages its own dialog state.
 */
function UserActionsCell({
  user,
  actions,
}: {
  user: UserType;
  actions: UsersTableActions;
}) {
  const [showInstallations, setShowInstallations] = useState(false);
  const navigate = useNavigate();
  const { can } = usePermissions();
  const isCurrentUser = user.uid === actions.currentUser?.uid;
  const canDownloadInstallations = can("installations:download_package");
  // Capacidad e idoneidad del dato son cosas distintas: solo instituciones y
  // médicos tienen ficha de infraestructura.
  const canViewServers = can("servers:view") && isCenterLike(user);

  return (
    <>
      <div
        className="flex items-center justify-end space-x-1"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Acceso a Imagenología */}
        {actions.onViewImagings && can("imagings:list") && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                actions.onViewImagings!(user);
              }}
              className="text-teal-600 hover:text-teal-700 hover:bg-teal-600/10 p-2 rounded-md transition-colors"
              title="Ver Imagenología"
            >
              <Monitor className="h-5 w-5" />
            </button>
          )}

        {canViewServers && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/servers/${user.uid}`);
            }}
            className="text-sky-600 hover:text-sky-700 hover:bg-sky-600/10 p-2 rounded-md transition-colors"
            title="Infraestructura / Servidores"
            aria-label="Infraestructura / Servidores"
          >
            <Server className="h-5 w-5" />
          </button>
        )}

        {/* Edición de Usuario */}
        {can("users:edit") && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              actions.onEdit(user);
            }}
            disabled={isCurrentUser}
            className="text-primary hover:text-primary/80 hover:bg-primary/10 p-2 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Editar usuario"
          >
            <Edit className="h-5 w-5" />
          </button>
        )}

        {/* Menú de acciones unificado */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
              title="Acciones de usuario"
            >
              <MoreHorizontal className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {/* Información y Edición */}
            <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground">
              <User className="h-3 w-3" /> Gestión
            </DropdownMenuLabel>

            <DropdownMenuItem
              onClick={() => actions.onEdit(user)}
              disabled={isCurrentUser}
            >
              <Edit className="h-4 w-4 mr-2 text-primary" />
              Editar perfil / email
            </DropdownMenuItem>

            {actions.onViewImagings && can("imagings:list") && (
                <DropdownMenuItem onClick={() => actions.onViewImagings!(user)}>
                  <Scan className="h-4 w-4 mr-2 text-teal-600" />
                  Imagenología
                </DropdownMenuItem>
              )}

            {/* Descargar Instalaciones — instituciones y médicos */}
            {isCenterLike(user) && canDownloadInstallations && (
                <DropdownMenuItem onClick={() => setShowInstallations(true)}>
                  <Package className="h-4 w-4 mr-2 text-blue-500" />
                  Descargar Instalaciones
                </DropdownMenuItem>
              )}

            {canViewServers && (
              <DropdownMenuItem
                onClick={() => navigate(`/servers/${user.uid}`)}
              >
                <Server className="h-4 w-4 mr-2 text-sky-600" />
                Infraestructura / Servidores
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {/* Acciones de Cuenta */}
            {can("users:toggle_active") && (
              <>
                <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Key className="h-3 w-3" /> Contraseña
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => actions.onAction(user, "reset-password-email")}
                >
                  <Mail className="h-4 w-4 mr-2 text-amber-500" />
                  Enviar enlace por correo
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => actions.onAction(user, "reset-password-direct")}
                >
                  <Key className="h-4 w-4 mr-2 text-amber-500" />
                  Generar contraseña directa
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Shield className="h-3 w-3" /> Cuenta
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => actions.onAction(user, "verify-email")}>
                  <Mail className="h-4 w-4 mr-2 text-green-500" />
                  Enviar verificación por correo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => actions.onAction(user, "verify-direct")}>
                  <ShieldCheck className="h-4 w-4 mr-2 text-green-500" />
                  Verificar cuenta directamente
                </DropdownMenuItem>
                {user.active ? (
                  <DropdownMenuItem
                    onClick={() => actions.onAction(user, "disable")}
                    className="text-red-600 focus:text-red-600"
                    disabled={isCurrentUser}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Deshabilitar cuenta
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => actions.onAction(user, "enable")}>
                    <Shield className="h-4 w-4 mr-2 text-blue-500" />
                    Habilitar cuenta
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
              </>
            )}

            {/* Gestión Avanzada (Solo admin) */}
            {can("api_keys:list") && (
              <>
                <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground">
                  <KeyRound className="h-3 w-3" /> API
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => actions.onAction(user, "api-keys")}>
                  <KeyRound className="h-4 w-4 mr-2 text-purple-500" />
                  Gestionar API Keys
                </DropdownMenuItem>

                <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground">
                  <KeyRound className="h-3 w-3" /> Licencias
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => actions.onAction(user, "licenses")}>
                  <KeyRound className="h-4 w-4 mr-2 text-blue-500" />
                  Gestionar Licencias
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Installations dialog */}
      {(user.type === "institution" || user.type === "physician") && (
        <UserInstallationsDialog
          user={user}
          open={showInstallations}
          onClose={() => setShowInstallations(false)}
        />
      )}
    </>
  );
}

export const createUsersColumns = (actions: UsersTableActions): ColumnDef<UserType>[] => [
  {
    accessorKey: "name",
    header: "Usuario",
    cell: ({ row }) => {
      const user = row.original;
      const isCurrentUser = user.uid === actions.currentUser?.uid;

      return (
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-medium text-primary">
              {getUserInitials(user)}
            </span>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium">
              {getUserFullName(user)}
              {isCurrentUser && (
                <Badge variant="secondary" className="ml-2">
                  Tú
                </Badge>
              )}
            </div>
            <div className="text-sm text-muted-foreground">{user.email}</div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "type",
    header: "Rol",
    cell: ({ row }) => {
      const user = row.original;
      return <Badge variant="secondary">{getUserRole(user)}</Badge>;
    },
  },
  {
    accessorKey: "created_at",
    header: "Registro",
    cell: ({ row }) => {
      const user = row.original;
      return (
        <span className="text-muted-foreground">{formatSimpleDate(user.created_at)}</span>
      );
    },
  },
  {
    accessorKey: "active",
    header: "Estado",
    cell: ({ row }) => {
      const user = row.original;
      const isActive = user.auth ? user.auth.active : user.active;
      return (
        <Badge variant={isActive ? "default" : "destructive"}>
          {isActive ? "Activo" : "Inactivo"}
        </Badge>
      );
    },
  },
  {
    accessorKey: "last_login",
    header: "Último acceso",
    cell: ({ row }) => {
      const user = row.original;
      const lastLogin = user.auth ? user.auth.last_login : user.last_login;
      return (
        <span className="text-muted-foreground">
          {formatDate(lastLogin || undefined)}
        </span>
      );
    },
  },
  {
    id: "actions",
    header: "Acciones",
    cell: ({ row }) => {
      const user = row.original;
      return <UserActionsCell user={user} actions={actions} />;
    },
  },
];
